<template>
  <ServicePage
    service-id="databases"
    :monthly-cost="monthlyCost"
    :item-count="lines.length"
    :can-add="canAdd"
    @add="addLines"
  >
    <div class="space-y-6">
      <!-- PostgreSQL / MySQL -->
      <ConfigSection
        section-key="postgres"
        title="PostgreSQL / MySQL"
        subtitle="Managed relational database, single AZ, next business day support"
        v-model:enabled="postgres.enabled"
        :monthly-cost="postgresLine ? postgresLine.monthlyCost : 0"
      >
        <div class="space-y-5">
          <InstancePicker
            :entries="postgresInstances"
            :selected="postgres.instance"
            @select="postgres.instance = $event"
          />
          <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <NumberField v-model="postgres.count" label="Number of instances" :min="1" />
            <NumberField
              v-model="postgres.storage"
              label="Storage per instance"
              unit="GB"
              :hint="unitHint(postgresStorage)"
            />
            <NumberField
              v-model="postgres.restore"
              label="Backup restore"
              unit="GB"
              :hint="unitHint(postgresRestore)"
            />
          </div>
        </div>
      </ConfigSection>

      <!-- Redis -->
      <ConfigSection
        section-key="redis"
        title="Redis"
        subtitle="Managed in-memory cache"
        v-model:enabled="redis.enabled"
        :monthly-cost="redisLine ? redisLine.monthlyCost : 0"
      >
        <div class="space-y-5">
          <InstancePicker
            :entries="redisInstances"
            :selected="redis.instance"
            @select="redis.instance = $event"
          />
          <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <NumberField v-model="redis.count" label="Number of instances" :min="1" />
          </div>
        </div>
      </ConfigSection>

      <!-- OpenSearch -->
      <ConfigSection
        section-key="opensearch"
        title="OpenSearch"
        subtitle="Managed search and analytics"
        v-model:enabled="opensearch.enabled"
        :monthly-cost="opensearchLine ? opensearchLine.monthlyCost : 0"
      >
        <div class="space-y-5">
          <InstancePicker
            :entries="opensearchInstances"
            :selected="opensearch.instance"
            @select="opensearch.instance = $event"
          />
          <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
            <NumberField v-model="opensearch.count" label="Number of deployments" :min="1" />
            <NumberField
              v-model="opensearch.storage"
              label="Storage per deployment"
              unit="GB"
              :hint="unitHint(opensearchStorage)"
            />
          </div>
        </div>
      </ConfigSection>

      <CostPreview v-if="lines.length" :lines-prop="lines" />
    </div>
  </ServicePage>
</template>

<script>
import { mapGetters } from "vuex";
import ServicePage from "@/components/config/ServicePage.vue";
import ConfigSection from "@/components/config/ConfigSection.vue";
import NumberField from "@/components/config/NumberField.vue";
import InstancePicker from "@/components/config/InstancePicker.vue";
import CostPreview from "@/components/config/CostPreview.vue";
import { configurable } from "@/mixins/configurable";
import { byType, estimateLine, lineItem, priceOf } from "@/utils/pricing";

/** Sizing entries are the ones billed per month; the rest are add-ons. */
const sizesOf = (list) =>
  (Array.isArray(list) ? list : []).filter((entry) => entry.unit === "month");

export default {
  name: "DatabasesService",
  components: { ServicePage, ConfigSection, NumberField, InstancePicker, CostPreview },
  mixins: [configurable],
  data() {
    return {
      postgres: { enabled: true, instance: null, count: 1, storage: 0, restore: 0 },
      redis: { enabled: false, instance: null, count: 1 },
      opensearch: { enabled: false, instance: null, count: 1, storage: 0 },
    };
  },
  computed: {
    ...mapGetters({ section: "pricing/section" }),

    postgresInstances() {
      return sizesOf(this.section("databases.postgres"));
    },
    postgresStorage() {
      return byType(this.section("databases.postgres"), "blockStorage");
    },
    postgresRestore() {
      return byType(this.section("databases.postgres"), "backupRestore");
    },
    redisInstances() {
      return sizesOf(this.section("databases.redis"));
    },
    opensearchInstances() {
      return sizesOf(this.section("databases.opensearch"));
    },
    opensearchStorage() {
      return byType(this.section("databases.opensearch"), "blockStorage");
    },

    postgresLine() {
      const { enabled, instance, count, storage, restore } = this.postgres;
      if (!enabled || !instance) return null;

      const items = [
        lineItem({
          label: `PostgreSQL/MySQL ${instance.type}`,
          detail: instance.spec,
          quantity: 1,
          unit: "month",
          unitPrice: priceOf(instance),
        }),
      ];

      if (storage > 0) {
        items.push(
          lineItem({
            label: "Database storage",
            detail: `${storage} GB`,
            quantity: storage,
            unit: "GB/month",
            unitPrice: priceOf(this.postgresStorage),
          })
        );
      }
      if (restore > 0) {
        items.push(
          lineItem({
            label: "Backup restore",
            detail: `${restore} GB`,
            quantity: restore,
            unit: "GB",
            unitPrice: priceOf(this.postgresRestore),
          })
        );
      }

      return estimateLine({
        key: "databases.postgres",
        serviceId: "databases",
        name: `Managed PostgreSQL/MySQL ${instance.type}`,
        description: instance.spec,
        config: { type: instance.type, storage, restore },
        items,
        quantity: Math.max(1, Math.floor(count || 1)),
      });
    },

    redisLine() {
      const { enabled, instance, count } = this.redis;
      if (!enabled || !instance) return null;

      return estimateLine({
        key: "databases.redis",
        serviceId: "databases",
        name: `Managed Redis ${instance.type}`,
        description: instance.spec,
        config: { type: instance.type },
        items: [
          lineItem({
            label: `Redis ${instance.type}`,
            detail: instance.spec,
            quantity: 1,
            unit: "month",
            unitPrice: priceOf(instance),
          }),
        ],
        quantity: Math.max(1, Math.floor(count || 1)),
      });
    },

    opensearchLine() {
      const { enabled, instance, count, storage } = this.opensearch;
      if (!enabled || !instance) return null;

      const items = [
        lineItem({
          label: `OpenSearch ${instance.type}`,
          detail: instance.spec,
          quantity: 1,
          unit: "month",
          unitPrice: priceOf(instance),
        }),
      ];

      if (storage > 0) {
        items.push(
          lineItem({
            label: "OpenSearch storage",
            detail: `${storage} GB`,
            quantity: storage,
            unit: "GB/month",
            unitPrice: priceOf(this.opensearchStorage),
          })
        );
      }

      return estimateLine({
        key: "databases.opensearch",
        serviceId: "databases",
        name: `Managed OpenSearch (${instance.type})`,
        description: instance.description || instance.spec,
        config: { type: instance.type, storage },
        items,
        quantity: Math.max(1, Math.floor(count || 1)),
      });
    },

    lines() {
      return [this.postgresLine, this.redisLine, this.opensearchLine].filter(
        (line) => line && line.monthlyCost > 0
      );
    },
  },
  watch: {
    postgresInstances: {
      immediate: true,
      handler(list) {
        if (!this.postgres.instance && list.length) this.postgres.instance = list[0];
      },
    },
    redisInstances: {
      immediate: true,
      handler(list) {
        if (!this.redis.instance && list.length) this.redis.instance = list[0];
      },
    },
    opensearchInstances: {
      immediate: true,
      handler(list) {
        if (!this.opensearch.instance && list.length) this.opensearch.instance = list[0];
      },
    },
  },
  methods: {
    unitHint(entry) {
      if (!entry) return "";
      return `${entry.description || ""} (${entry.unit})`.trim();
    },
  },
};
</script>
